/* eslint-disable no-console */
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { LucideProps } from 'lucide-react';
import {
  Search,
  UploadCloud,
  FileText,
  FileImage,
  FileSpreadsheet,
  MessageSquareText,
  History,
  AlertCircle,
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  Mic,
  SlidersHorizontal,
} from 'lucide-react';
import styles from './DocumentIntelligencePatientDetail.module.css';

import { useClinic } from '../../../components/ClinicProvider';
import AiGradient from '../../EmbryoGradingAI/AiGradient';
import AiLoader from '../../EmbryoGradingAI/AiLoader';
import { toast } from 'react-toastify';
import type {
  DocumentItem,
  DocumentKind,
  ExtractReportResponse,
  NavState,
  PatientSummary,
  Props,
  QuestionnaireField,
  QuestionnaireSection,
  TabKey,
  SummaryLogItem,
  ParsedSummaryAiNotes,
} from './DocumentIntelligencePatientDetail.types';

import {
  extractQuestionnaireFromDocument,
  filterDocuments,
  findPatientById,
  getDateRangeForLastMonth,
  getInitials,
  isExtractableDocument,
  isPreviewableDocument,
  mapPatientToSummary,
  mapUploadedFilesToDocuments,
  saveExtractedDocumentIntelligenceToEMR,
  saveDocumentIntelligenceSummaryToNotes,
  buildDocumentIntelligenceSummaryNotesString,
  checkPatientHasActiveCycle,
  fetchPatientSummaryLogs,
  parseSummaryAiNotes,
  resolveSummaryLogCreatedAt,
  formatCommunicationDateLabel,
  formatCommunicationTimeLabel,
  getSummaryLogTitle,
  getSummaryLogSnippet,
} from './DocumentIntelligencePatientDetail.api';
import EmbeddedVoiceToTextWorkbench from './EmbeddedVoiceToTextWorkbench';

type SidebarMode = 'idle' | 'loading' | 'success' | 'error';
type SuccessView = 'summary' | 'fields';
type EditableFieldMap = Record<string, string>;

type ExtractionPageOption = {
  key: string;
  label: string;
  description: string;
  documentType: string;
};

type DisplayQuestionnaireField = QuestionnaireField & {
  displayLabel: string;
};

type StructuredFieldGroup = {
  key: string;
  title: string;
  order: number;
  fields: DisplayQuestionnaireField[];
};

type StructuredQuestionnaireSection = {
  section_key: string;
  section_title: string;
  display_order?: number;
  directFields: DisplayQuestionnaireField[];
  groups: StructuredFieldGroup[];
};

type CommunicationLogType =
  | 'call'
  | 'video_call'
  | 'whatsapp'
  | 'email'
  | 'voice_to_text';

type CommunicationFilterKey = 'all' | CommunicationLogType;
type CommunicationToolKey = CommunicationLogType;

type EnrichedCommunicationLog = SummaryLogItem & {
  rowKey: string;
  parsedAiNotes: ParsedSummaryAiNotes | null;
  createdAtValue: string;
  dateLabel: string;
  timeLabel: string;
  titleText: string;
  snippetText: string;
  communicationType: CommunicationLogType;
};

const COMMUNICATION_CHANNEL_OPTIONS: Array<{
  key: CommunicationLogType;
  label: string;
  icon: React.ComponentType<LucideProps>;
  enabled: boolean;
}> = [
  // { key: 'call', label: 'Call', icon: PhoneCall, enabled: false },
  // { key: 'video_call', label: 'Video Call', icon: Video, enabled: false },
  // { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, enabled: false },
  // { key: 'email', label: 'Email', icon: Mail, enabled: false },
  { key: 'voice_to_text', label: 'Voice to Text', icon: Mic, enabled: true },
];

const COMMUNICATION_FILTER_OPTIONS: Array<{
  key: CommunicationFilterKey;
  label: string;
}> = [
  { key: 'all', label: 'All' },
  ...COMMUNICATION_CHANNEL_OPTIONS.map((item) => ({
    key: item.key,
    label: item.label,
  })),
];

const COMMUNICATION_TOOL_OPTIONS: Array<{
  key: CommunicationToolKey;
  label: string;
  icon: React.ComponentType<LucideProps>;
  enabled: boolean;
}> = [...COMMUNICATION_CHANNEL_OPTIONS];

const isVoiceToTextLog = (log: SummaryLogItem): boolean => {
  return (
    String(log.type || '')
      .trim()
      .toLowerCase() === 'voice_to_text'
  );
};

const resolveCommunicationType = (
  log: SummaryLogItem,
): CommunicationLogType | null => {
  const normalizedType = String(log.type || '')
    .trim()
    .toLowerCase();

  if (normalizedType === 'voice_to_text') return 'voice_to_text';
  if (normalizedType === 'call') return 'call';
  if (normalizedType === 'video_call') return 'video_call';
  if (normalizedType === 'whatsapp') return 'whatsapp';
  if (normalizedType === 'email') return 'email';

  return null;
};

const EXTRACTION_PAGE_OPTIONS: ExtractionPageOption[] = [
  {
    key: 'medical_history',
    label: 'Medical History',
    description:
      'Past and current illnesses, diagnoses, and clinical conditions.',
    documentType: 'medical_history',
  },
  // {
  //   key: "oocyte_grading",
  //   label: "Oocyte Grading",
  //   description: "Assessment of oocyte quality and developmental potential.",
  //   documentType: "oocyte_grading",
  // },
  {
    key: 'embryo_culture',
    label: 'Embryo Culture',
    description: 'Assessment of embryo quality and developmental potential.',
    documentType: 'embryo_grading',
  },
];

const DocumentIcon: React.FC<{ kind: DocumentKind }> = ({ kind }) => {
  if (kind === 'pdf') {
    return (
      <div className={`${styles.docIconBox} ${styles.pdfIcon}`}>
        <FileText size={18} />
      </div>
    );
  }

  if (kind === 'sheet') {
    return (
      <div className={`${styles.docIconBox} ${styles.sheetIcon}`}>
        <FileSpreadsheet size={18} />
      </div>
    );
  }

  if (kind === 'image') {
    return (
      <div className={`${styles.docIconBox} ${styles.imageIcon}`}>
        <FileImage size={18} />
      </div>
    );
  }

  return (
    <div className={`${styles.docIconBox} ${styles.docIcon}`}>
      <FileText size={18} />
    </div>
  );
};

const AvatarBadge: React.FC<{
  image?: string;
  name: string;
  className?: string;
}> = ({ image, name, className = '' }) => {
  return (
    <div className={`${styles.avatarWrap} ${className}`}>
      {image ? (
        <img src={image} alt={name} className={styles.avatarImage} />
      ) : (
        <div className={styles.avatarFallback}>{getInitials(name)}</div>
      )}
    </div>
  );
};

const toDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'object' && item !== null
          ? JSON.stringify(item)
          : String(item),
      )
      .join(', ');
  }

  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
};

const sortByDisplayOrder = <T extends { display_order?: number }>(
  items: T[],
): T[] => {
  return [...items].sort(
    (a, b) =>
      (a.display_order ?? Number.MAX_SAFE_INTEGER) -
      (b.display_order ?? Number.MAX_SAFE_INTEGER),
  );
};

const normalizeSections = (
  response: ExtractReportResponse | null,
): QuestionnaireSection[] => {
  if (!response?.sections?.length) return [];

  return sortByDisplayOrder(response.sections).map((section) => ({
    ...section,
    fields: sortByDisplayOrder(
      Array.isArray(section.fields) ? section.fields : [],
    ),
  }));
};

const buildEditableFieldMap = (
  sections: QuestionnaireSection[],
): EditableFieldMap => {
  const map: EditableFieldMap = {};

  sections.forEach((section) => {
    section.fields.forEach((field) => {
      map[field.field_key] = toDisplayValue(field.value);
    });
  });

  return map;
};

const getOrderedSectionsForSummary = (
  sections: QuestionnaireSection[],
  selectedType: ExtractionPageOption | null,
) => {
  if (!selectedType) return sections;

  return [...sections].sort((a, b) => {
    const aPriority = a.section_key === selectedType.documentType ? 0 : 1;
    const bPriority = b.section_key === selectedType.documentType ? 0 : 1;

    if (aPriority !== bPriority) return aPriority - bPriority;

    return (
      (a.display_order ?? Number.MAX_SAFE_INTEGER) -
      (b.display_order ?? Number.MAX_SAFE_INTEGER)
    );
  });
};

const fieldToSummarySentence = (
  field: QuestionnaireField,
  patientName: string,
): string | null => {
  const value = toDisplayValue(field.value).trim();
  if (!value) return null;

  const label = (field.label || field.field_key || '').trim();

  if (!label) return null;

  if (/patient name/i.test(label)) {
    return `Patient ${value} has been identified from the uploaded document.`;
  }

  if (/trying to conceive since/i.test(label)) {
    return `${patientName !== '-' ? patientName : 'Patient'} has been trying to conceive for ${value}.`;
  }

  if (/partner issue/i.test(label)) {
    return `Partner issue noted: ${value}.`;
  }

  if (/amh/i.test(label)) {
    return `AMH is ${value}.`;
  }

  if (/protocol/i.test(label)) {
    return `Protocol type suggested: ${value}.`;
  }

  if (/medication/i.test(label)) {
    return `Medication noted: ${value}.`;
  }

  return `${label} is ${value}.`;
};

const buildAiSummaryBullets = (
  response: ExtractReportResponse | null,
  sections: QuestionnaireSection[],
  selectedType: ExtractionPageOption | null,
  patientName: string,
): string[] => {
  if (!response || !sections.length) return [];

  const orderedSections = getOrderedSectionsForSummary(sections, selectedType);
  const answeredFields = orderedSections.flatMap((section) =>
    section.fields.filter((field) => {
      const answerState = (field.ai_metadata?.answer_state || '').toUpperCase();
      return answerState === 'ANSWERED' && toDisplayValue(field.value).trim();
    }),
  );

  const bullets: string[] = [];

  for (const field of answeredFields) {
    const sentence = fieldToSummarySentence(field, patientName);
    if (!sentence) continue;
    if (!bullets.includes(sentence)) bullets.push(sentence);
    if (bullets.length >= 4) break;
  }

  if (!bullets.length) {
    bullets.push(
      `AI extraction completed for ${selectedType?.label || response.document_type || 'the selected type'}.`,
    );
  }

  if (bullets.length < 4) {
    const answered = response.meta?.fields_answered ?? answeredFields.length;
    const unanswered =
      response.meta?.fields_unanswered ??
      orderedSections
        .flatMap((section) => section.fields)
        .filter((field) => {
          const answerState = (
            field.ai_metadata?.answer_state || ''
          ).toUpperCase();
          return answerState === 'UNANSWERED';
        }).length;

    bullets.push(
      `The document returned ${answered} answered field${answered !== 1 ? 's' : ''} and ${unanswered} unanswered field${unanswered !== 1 ? 's' : ''}.`,
    );
  }

  return bullets.slice(0, 4);
};

const getInputTypeForField = (
  field: QuestionnaireField,
): React.HTMLInputTypeAttribute => {
  const uiType = (field.ui_type || '').toLowerCase();
  const dataType = (field.data_type || '').toLowerCase();

  if (uiType === 'number' || dataType === 'integer' || dataType === 'number') {
    return 'number';
  }

  return 'text';
};

const getAnswerStateClassName = (state?: string): string => {
  const normalized = (state || '').toUpperCase();

  if (normalized === 'ANSWERED') return styles.answerStateAnswered;
  if (normalized === 'UNANSWERED') return styles.answerStateUnanswered;
  if (normalized === 'PARTIAL') return styles.answerStatePartial;

  return styles.answerStateUnknown;
};

const getAnswerStateLabel = (state?: string): string => {
  if (!state) return 'Unknown';
  return state.replace(/_/g, ' ');
};

const normalizeDisplayLabel = (label: string): string => {
  return label.replace(/\s+/g, ' ').trim();
};

const getLastFieldKeySegment = (fieldKey: string): string => {
  const clean = fieldKey.split('.').pop() || fieldKey;

  return clean
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};

const getGroupTitleFromField = (field: QuestionnaireField): string | null => {
  const label = normalizeDisplayLabel(field.label || field.field_key || '');

  if (!label || !label.includes('>')) return null;

  return label.split('>').map((part) => part.trim())[0] || null;
};

const getChildLabelFromField = (field: QuestionnaireField): string => {
  const label = normalizeDisplayLabel(field.label || '');

  if (!label) return getLastFieldKeySegment(field.field_key);

  if (!label.includes('>')) return label;

  return label
    .split('>')
    .slice(1)
    .map((part) => part.trim())
    .join(' > ');
};

const isVirtualGroupHeaderField = (field: QuestionnaireField): boolean => {
  const label = normalizeDisplayLabel(field.label || '');
  const valueText = toDisplayValue(field.value).trim();
  const hasOptions = Array.isArray(field.options) && field.options.length > 0;

  return Boolean(label) && !label.includes('>') && !valueText && !hasOptions;
};

const addIndexedLabels = (
  fields: DisplayQuestionnaireField[],
): DisplayQuestionnaireField[] => {
  const counts = new Map<string, number>();

  fields.forEach((field) => {
    const key = field.displayLabel.trim().toLowerCase();
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  const seen = new Map<string, number>();

  return fields.map((field) => {
    const key = field.displayLabel.trim().toLowerCase();
    const total = counts.get(key) || 0;

    if (total <= 1) return field;

    const nextIndex = (seen.get(key) || 0) + 1;
    seen.set(key, nextIndex);

    return {
      ...field,
      displayLabel: `${field.displayLabel} ${nextIndex}`,
    };
  });
};

const showSuccessToast = (message: string) => {
  toast(
    <div className={styles.successToastContent}>
      <span className={styles.successToastIconWrap}>
        <Check size={16} strokeWidth={3} />
      </span>
      <span className={styles.successToastText}>{message}</span>
    </div>,
    {
      icon: false,
      closeButton: false,
      hideProgressBar: true,
      className: styles.successToastRoot,
      position: 'top-right',
      autoClose: 2600,
    },
  );
};

const showErrorToast = (message: string) => {
  toast(
    <div className={styles.errorToastContent}>
      <span className={styles.errorToastIconWrap}>
        <AlertCircle size={16} strokeWidth={3} />
      </span>
      <span className={styles.errorToastText}>{message}</span>
    </div>,
    {
      icon: false,
      closeButton: false,
      hideProgressBar: true,
      className: styles.errorToastRoot,
      position: 'top-right',
      autoClose: 3200,
    },
  );
};

const buildStructuredSections = (
  sections: QuestionnaireSection[],
): StructuredQuestionnaireSection[] => {
  return sortByDisplayOrder(sections).map((section) => {
    const sortedFields = sortByDisplayOrder(
      Array.isArray(section.fields) ? section.fields : [],
    );

    const groupsMap = new Map<string, StructuredFieldGroup>();
    const directFields: DisplayQuestionnaireField[] = [];

    sortedFields.forEach((field) => {
      const baseLabel = normalizeDisplayLabel(
        field.label || field.field_key || '',
      );
      if (!baseLabel) return;

      if (isVirtualGroupHeaderField(field)) {
        if (!groupsMap.has(baseLabel)) {
          groupsMap.set(baseLabel, {
            key: `${section.section_key}.${baseLabel}`,
            title: baseLabel,
            order: field.display_order ?? Number.MAX_SAFE_INTEGER,
            fields: [],
          });
        }
        return;
      }

      const groupTitle = getGroupTitleFromField(field);
      const displayField: DisplayQuestionnaireField = {
        ...field,
        displayLabel: groupTitle
          ? getChildLabelFromField(field)
          : baseLabel || getLastFieldKeySegment(field.field_key),
      };

      if (groupTitle) {
        const normalizedGroupTitle = normalizeDisplayLabel(groupTitle);
        const existing = groupsMap.get(normalizedGroupTitle);

        if (existing) {
          existing.fields.push(displayField);
        } else {
          groupsMap.set(normalizedGroupTitle, {
            key: `${section.section_key}.${normalizedGroupTitle}`,
            title: normalizedGroupTitle,
            order: field.display_order ?? Number.MAX_SAFE_INTEGER,
            fields: [displayField],
          });
        }
      } else {
        directFields.push(displayField);
      }
    });

    const finalDirectFields = addIndexedLabels(
      sortByDisplayOrder(directFields),
    );

    const groups = Array.from(groupsMap.values())
      .map((group) => ({
        ...group,
        fields: addIndexedLabels(sortByDisplayOrder(group.fields)),
      }))
      .sort((a, b) => a.order - b.order);

    return {
      section_key: section.section_key,
      section_title: section.section_title,
      display_order: section.display_order,
      directFields: finalDirectFields,
      groups,
    };
  });
};

const DocumentIntelligencePatientDetail: React.FC<Props> = ({
  patientId: patientIdProp,
  isModal = false,
  onClose,
}) => {
  const { ClinicId } = useClinic();
  const { patientId: patientIdFromParams } = useParams<{ patientId: string }>();
  const location = useLocation();
  const navState = (location.state as NavState | null) ?? {};
  const [isSavingSummary, setIsSavingSummary] = useState(false);
  const [isSavingToEmr, setIsSavingToEmr] = useState(false);
  const [communicationLogs, setCommunicationLogs] = useState<SummaryLogItem[]>(
    [],
  );
  const [communicationLoading, setCommunicationLoading] = useState(false);
  const [communicationError, setCommunicationError] = useState<string | null>(
    null,
  );
  const [communicationFilter, setCommunicationFilter] =
    useState<CommunicationFilterKey>('all');
  const [communicationSearch, setCommunicationSearch] = useState('');
  const [activeCommunicationTool, setActiveCommunicationTool] =
    useState<CommunicationToolKey>('voice_to_text');
  const [selectedCommunicationLogId, setSelectedCommunicationLogId] = useState<
    string | null
  >(null);
  const [communicationTranscriptSeed, setCommunicationTranscriptSeed] =
    useState('');
  const [voiceWorkbenchSeedKey, setVoiceWorkbenchSeedKey] = useState(0);

  const [isTranscriptModalOpen, setIsTranscriptModalOpen] = useState(false);
  const [transcriptModalLog, setTranscriptModalLog] =
    useState<EnrichedCommunicationLog | null>(null);
  const [transcriptModalValue, setTranscriptModalValue] = useState('');
  const [transcriptDraftMap, setTranscriptDraftMap] = useState<
    Record<string, string>
  >({});
  const resolvedPatientId = useMemo(() => {
    return String(
      patientIdProp ?? patientIdFromParams ?? navState.patientId ?? '',
    ).trim();
  }, [patientIdProp, patientIdFromParams, navState.patientId]);

  const [patientSummary, setPatientSummary] = useState<PatientSummary>({
    id: '-',
    name: '-',
    patientAvatar: '',
    partnerAvatar: '',
    age: '-',
    sexAssignedAtBirth: '-',
    mrn: '-',
    allergy: '-',
    lastModified: '-',
    doctor: '-',
  });

  const [activeTab, setActiveTab] = useState<TabKey>('scan');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchDocument, setSearchDocument] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState<DocumentItem[]>(
    [],
  );
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );

  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('idle');
  const [successView, setSuccessView] = useState<SuccessView>('summary');
  const [searchValue, setSearchValue] = useState('');
  const [selectedType, setSelectedType] = useState<ExtractionPageOption | null>(
    null,
  );
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [extractionResponse, setExtractionResponse] =
    useState<ExtractReportResponse | null>(null);
  const [editableFieldValues, setEditableFieldValues] =
    useState<EditableFieldMap>({});
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadedDocsRef = useRef<DocumentItem[]>([]);
  const { startDate, endDate } = useMemo(() => getDateRangeForLastMonth(), []);
  const [isCheckingCycle, setIsCheckingCycle] = useState(false);
  const [showNoActiveCycleModal, setShowNoActiveCycleModal] = useState(false);

  useEffect(() => {
    uploadedDocsRef.current = uploadedDocuments;
  }, [uploadedDocuments]);

  useEffect(() => {
    return () => {
      uploadedDocsRef.current.forEach((doc) => {
        if (doc.previewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(doc.previewUrl);
        }
      });
    };
  }, []);

  const loadCommunicationLogs = useCallback(async () => {
    if (!resolvedPatientId) return;

    try {
      setCommunicationLoading(true);
      setCommunicationError(null);

      const response = await fetchPatientSummaryLogs(resolvedPatientId);

      const voiceLogs = Array.isArray(response)
        ? response.filter(isVoiceToTextLog)
        : [];

      setCommunicationLogs(voiceLogs);
    } catch (err) {
      console.error(err);
      setCommunicationError(
        err instanceof Error
          ? err.message
          : 'Failed to load communication logs.',
      );
    } finally {
      setCommunicationLoading(false);
    }
  }, [resolvedPatientId]);

  useEffect(() => {
    if (activeTab !== 'communication') return;
    void loadCommunicationLogs();
  }, [activeTab, loadCommunicationLogs]);

  useEffect(() => {
    if (!ClinicId || !resolvedPatientId) return;

    const loadPatient = async () => {
      setLoading(true);
      setError(null);

      try {
        const patient = await findPatientById(
          ClinicId,
          resolvedPatientId,
          startDate,
          endDate,
        );

        setPatientSummary(mapPatientToSummary(patient));
      } catch (err) {
        setError(`Failed to load patient details: ${err}`);
      } finally {
        setLoading(false); 
      }
    };

    void loadPatient();
  }, [ClinicId, resolvedPatientId, startDate, endDate]);

  const allDocuments = useMemo(() => {
    return [...uploadedDocuments];
  }, [uploadedDocuments]);

  const filteredDocumentList = useMemo(() => {
    return filterDocuments(allDocuments, searchDocument);
  }, [allDocuments, searchDocument]);

  const selectedDocument = useMemo(() => {
    return allDocuments.find((doc) => doc.id === selectedDocumentId) ?? null;
  }, [allDocuments, selectedDocumentId]);

  const filteredOptions = useMemo(() => {
    const q = searchValue.trim().toLowerCase();

    if (!q) return EXTRACTION_PAGE_OPTIONS;

    return EXTRACTION_PAGE_OPTIONS.filter(
      (option) =>
        option.label.toLowerCase().includes(q) ||
        option.description.toLowerCase().includes(q),
    );
  }, [searchValue]);

  const normalizedSections = useMemo(() => {
    return normalizeSections(extractionResponse);
  }, [extractionResponse]);

  const structuredSections = useMemo(() => {
    return buildStructuredSections(normalizedSections);
  }, [normalizedSections]);

  const aiSummaryBullets = useMemo(() => {
    return buildAiSummaryBullets(
      extractionResponse,
      normalizedSections,
      selectedType,
      patientSummary.name,
    );
  }, [
    extractionResponse,
    normalizedSections,
    selectedType,
    patientSummary.name,
  ]);

  const requiresActiveCycleCheck =
    selectedType?.documentType === 'embryo_grading' ||
    selectedType?.documentType === 'oocyte_grading';

  useEffect(() => {
    if (!selectedDocumentId && uploadedDocuments.length) {
      setSelectedDocumentId(uploadedDocuments[0].id);
    }
  }, [uploadedDocuments, selectedDocumentId]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const resetToSelection = () => {
    setSidebarMode('idle');
    setSuccessView('summary');
    setSelectedType(null);
    setExtractionError(null);
    setExtractionResponse(null);
    setEditableFieldValues({});
    setInfoMessage(null);
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) return;

    const supportedFiles = files.filter((file) => {
      const name = file.name.toLowerCase();
      return (
        file.type === 'application/pdf' ||
        file.type === 'image/png' ||
        file.type === 'image/jpeg' ||
        name.endsWith('.pdf') ||
        name.endsWith('.png') ||
        name.endsWith('.jpg') ||
        name.endsWith('.jpeg')
      );
    });

    if (!supportedFiles.length) {
      setSidebarMode('error');
      setExtractionError(
        'Only PDF, PNG, and JPEG files are supported for questionnaire extraction.',
      );
      event.target.value = '';
      return;
    }

    const mappedFiles = mapUploadedFilesToDocuments(supportedFiles);

    setUploadedDocuments((prev) => [...mappedFiles, ...prev]);
    setSelectedDocumentId(mappedFiles[0]?.id ?? null);
    setActiveTab('scan');
    resetToSelection();
    event.target.value = '';
  };

  const handleSelectDocument = (docId: string) => {
    setSelectedDocumentId(docId);
    resetToSelection();
  };

  const handleBackToLibrary = () => {
    setSelectedDocumentId(null);
    resetToSelection();
  };

  const enrichedCommunicationLogs = useMemo<EnrichedCommunicationLog[]>(() => {
    return communicationLogs
      .filter(isVoiceToTextLog)
      .map((log, index) => {
        const parsedAiNotes = parseSummaryAiNotes(log.ai_notes);
        const createdAtValue = resolveSummaryLogCreatedAt(log, parsedAiNotes);
        const communicationType = resolveCommunicationType(log);

        if (!communicationType) {
          return null;
        }

        return {
          ...log,
          rowKey: `${createdAtValue || 'row'}-${index}`,
          parsedAiNotes,
          createdAtValue,
          dateLabel: formatCommunicationDateLabel(createdAtValue),
          timeLabel: formatCommunicationTimeLabel(createdAtValue),
          titleText: getSummaryLogTitle(log, parsedAiNotes),
          snippetText: getSummaryLogSnippet(log, parsedAiNotes),
          communicationType,
        };
      })
      .filter(Boolean) as EnrichedCommunicationLog[];
  }, [communicationLogs]);

  const filteredCommunicationLogs = useMemo(() => {
    let logs = [...enrichedCommunicationLogs];

    if (communicationFilter !== 'all') {
      logs = logs.filter(
        (log) => log.communicationType === communicationFilter,
      );
    }

    const q = communicationSearch.trim().toLowerCase();

    if (!q) return logs;

    return logs.filter((log) => {
      return (
        log.titleText.toLowerCase().includes(q) ||
        log.snippetText.toLowerCase().includes(q) ||
        String(log.source || '')
          .toLowerCase()
          .includes(q) ||
        String(log.doctor_name || '')
          .toLowerCase()
          .includes(q)
      );
    });
  }, [enrichedCommunicationLogs, communicationFilter, communicationSearch]);

  const groupedCommunicationLogs = useMemo(() => {
    const map = new Map<string, EnrichedCommunicationLog[]>();

    filteredCommunicationLogs.forEach((log) => {
      const existing = map.get(log.dateLabel) || [];
      existing.push(log);
      map.set(log.dateLabel, existing);
    });

    return Array.from(map.entries()).map(([label, items]) => ({
      label,
      items,
    }));
  }, [filteredCommunicationLogs]);

  useEffect(() => {
    if (!filteredCommunicationLogs.length) {
      setSelectedCommunicationLogId(null);
      return;
    }

    if (
      selectedCommunicationLogId &&
      filteredCommunicationLogs.some(
        (log) => log.rowKey === selectedCommunicationLogId,
      )
    ) {
      return;
    }

    setSelectedCommunicationLogId(filteredCommunicationLogs[0].rowKey);
  }, [filteredCommunicationLogs, selectedCommunicationLogId]);

  const runExtraction = async (option: ExtractionPageOption) => {
    if (!selectedDocument) {
      setSidebarMode('error');
      setExtractionError('Please upload and select a document first.');
      return;
    }

    if (!selectedDocument.file) {
      setSidebarMode('error');
      setSelectedType(option);
      setExtractionError(
        'This document is a recent placeholder item. Please upload the actual PDF or image file to extract data.',
      );
      return;
    }

    if (!isExtractableDocument(selectedDocument)) {
      setSidebarMode('error');
      setSelectedType(option);
      setExtractionError('Only PDF, PNG, and JPEG files can be extracted.');
      return;
    }

    try {
      setSelectedType(option);
      setSidebarMode('loading');
      setSuccessView('summary');
      setExtractionError(null);
      setExtractionResponse(null);
      setEditableFieldValues({});
      setInfoMessage(null);

      const data = await extractQuestionnaireFromDocument({
        document: selectedDocument.file,
        documentId: selectedDocument.id,
        patientId:
          patientSummary.id !== '-' ? patientSummary.id : resolvedPatientId,
        responseVersion: 'v3',
        includeUnanswered: true,
        documentType: option.key,
      });

      const sections = normalizeSections(data);

      setExtractionResponse(data);
      setEditableFieldValues(buildEditableFieldMap(sections));
      setSidebarMode('success');
      setSuccessView('summary');
    } catch (err) {
      setSidebarMode('error');
      setExtractionError(
        err instanceof Error
          ? err.message
          : 'Failed to extract questionnaire data',
      );
    }
  };

  const handleRetry = async () => {
    if (!selectedType) {
      resetToSelection();
      return;
    }

    await runExtraction(selectedType);
  };

  const handleFieldChange = (fieldKey: string, value: string) => {
    setEditableFieldValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleSaveSummaryToNotes = async () => {
    if (!aiSummaryBullets.length || !selectedType || !extractionResponse) {
      showErrorToast('No summary is available to save.');
      return;
    }

    const patientIdToUse =
      patientSummary.id !== '-' ? patientSummary.id : resolvedPatientId;

    if (!patientIdToUse) {
      showErrorToast('Patient ID is missing. Unable to save summary.');
      return;
    }

    try {
      setIsSavingSummary(true);
      setInfoMessage(null);

      const aiNotes = buildDocumentIntelligenceSummaryNotesString({
        selectedTypeLabel: selectedType.label,
        documentType: selectedType.documentType,
        patientId: String(patientIdToUse),
        documentTitle: selectedDocument?.title,
        summaryBullets: aiSummaryBullets,
        extractionResponse,
        editableFieldValues,
        reason: 'manual_summary',
        cycleActive: null,
      });

      await saveDocumentIntelligenceSummaryToNotes({
        patientId: String(patientIdToUse),
        aiNotes,
        duration: 0,
        type: 'document',
        document_name: selectedDocument?.title,
        document_type: selectedType.documentType,
        doctor_name: patientSummary.doctor,
        source: selectedType.label,
        call_trascript: '',
      });

      console.log(selectedType);
      console.log(extractionResponse);
      showSuccessToast('Summary saved successfully to notes.');
    } catch (err) {
      showErrorToast(
        err instanceof Error ? err.message : 'Failed to save summary to notes.',
      );
    } finally {
      setIsSavingSummary(false);
    }
  };

  const handleCloseNoCycleModal = () => {
    if (isSavingSummary) return;
    setShowNoActiveCycleModal(false);
  };

  const handleSaveToEMR = async () => {
    if (!extractionResponse || !selectedType) {
      showErrorToast('No extracted data is available to save.');
      return;
    }

    const patientIdToUse =
      patientSummary.id !== '-' ? patientSummary.id : resolvedPatientId;

    if (!patientIdToUse) {
      showErrorToast('Patient ID is missing. Unable to save to EMR.');
      return;
    }

    try {
      setIsSavingToEmr(true);

      if (requiresActiveCycleCheck) {
        setIsCheckingCycle(true);

        const hasActiveCycle = await checkPatientHasActiveCycle(patientIdToUse);

        if (!hasActiveCycle) {
          setShowNoActiveCycleModal(true);
          return;
        }
      }

      await saveExtractedDocumentIntelligenceToEMR({
        patientId: patientIdToUse,
        documentType: selectedType.documentType,
        extractionResponse,
        editableFieldValues,
      });

      showSuccessToast('Extracted data stored successfully in EMR.');
    } catch (err) {

      showErrorToast(
        err instanceof Error
          ? err.message
          : 'Failed to save extracted data to EMR.',
      );
    } finally {
      setIsSavingToEmr(false);
      setIsCheckingCycle(false);
    }
  };

  const handleSaveToSummaryBecauseNoActiveCycle = async () => {
    if (!selectedType || !extractionResponse) {
      showErrorToast('No extracted data is available to save.');
      return;
    }

    const patientIdToUse =
      patientSummary.id !== '-' ? patientSummary.id : resolvedPatientId;

    if (!patientIdToUse) {
      showErrorToast('Patient ID is missing. Unable to save summary.');
      return;
    }

    try {
      setIsSavingSummary(true);

      const aiNotes = buildDocumentIntelligenceSummaryNotesString({
        selectedTypeLabel: selectedType.label,
        documentType: selectedType.documentType,
        patientId: patientIdToUse,
        documentTitle: selectedDocument?.title,
        summaryBullets: aiSummaryBullets,
        extractionResponse,
        editableFieldValues,
        reason: 'no_active_cycle_fallback',
        cycleActive: false,
      });

      await saveDocumentIntelligenceSummaryToNotes({
        patientId: String(patientIdToUse),
        aiNotes,
        duration: 0,
        type: 'document',
        document_name: selectedDocument?.title,
        document_type: selectedType.documentType,
        doctor_name: patientSummary.doctor,
        source: selectedType.label,
        call_trascript: '',
      });

      setShowNoActiveCycleModal(false);
      showSuccessToast('Saved to summary successfully.');
    } catch (err) {
      showErrorToast(
        err instanceof Error ? err.message : 'Failed to save to summary.',
      );
    } finally {
      setIsSavingSummary(false);
    }
  };

  const handleSelectCommunicationLog = (log: EnrichedCommunicationLog) => {
    setSelectedCommunicationLogId(log.rowKey);
  };

  const handleOpenTranscriptModal = (log: EnrichedCommunicationLog) => {
    setSelectedCommunicationLogId(log.rowKey);
    setTranscriptModalLog(log);
    setTranscriptModalValue(
      transcriptDraftMap[log.rowKey] ?? log.call_transcript ?? '',
    );
    setIsTranscriptModalOpen(true);
  };

  const handleCloseTranscriptModal = () => {
    setIsTranscriptModalOpen(false);
    setTranscriptModalLog(null);
    setTranscriptModalValue('');
  };

  const handleSaveTranscriptModal = () => {
    if (!transcriptModalLog) return;

    setTranscriptDraftMap((prev) => ({
      ...prev,
      [transcriptModalLog.rowKey]: transcriptModalValue,
    }));

    showSuccessToast('Transcript saved successfully.');
    handleCloseTranscriptModal();
  };

  const handleScanTranscriptFromModal = () => {
    if (!transcriptModalLog) return;

    const nextTranscript = transcriptModalValue.trim();

    if (!nextTranscript) {
      showErrorToast('Transcript is empty. Unable to scan.');
      return;
    }

    setTranscriptDraftMap((prev) => ({
      ...prev,
      [transcriptModalLog.rowKey]: transcriptModalValue,
    }));

    setActiveCommunicationTool('voice_to_text');
    setCommunicationTranscriptSeed(nextTranscript);
    setVoiceWorkbenchSeedKey((prev) => prev + 1);
    handleCloseTranscriptModal();
  };

  const handleSelectCommunicationTool = (tool: CommunicationToolKey) => {
    const matchedTool = COMMUNICATION_TOOL_OPTIONS.find(
      (item) => item.key === tool,
    );

    if (!matchedTool?.enabled) return;

    setActiveCommunicationTool(tool);

    if (tool === 'voice_to_text' && !communicationTranscriptSeed.trim()) {
      setVoiceWorkbenchSeedKey((prev) => prev + 1);
    }
  };

  const renderStructuredField = (field: DisplayQuestionnaireField) => {
    const fieldValue = editableFieldValues[field.field_key] ?? '';
    const answerState = field.ai_metadata?.answer_state;

    const isSelect =
      (field.ui_type || '').toLowerCase() === 'select' &&
      Array.isArray(field.options) &&
      field.options.length > 0;

    const isTextarea =
      (field.ui_type || '').toLowerCase() === 'textarea' ||
      ((field.ui_type || '').toLowerCase() === 'text' &&
        fieldValue.length > 60);

    return (
      <div key={field.field_key} className={styles.formFieldBlock}>
        <div className={styles.formFieldTopRow}>
          <label className={styles.formFieldLabel}>
            {field.displayLabel || field.label || field.field_key}
          </label>

          <span
            className={`${styles.answerStateBadge} ${getAnswerStateClassName(
              answerState,
            )}`}
          >
            {getAnswerStateLabel(answerState)}
          </span>
        </div>

        {isSelect ? (
          <select
            className={styles.formInput}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
          >
            <option value="">Select</option>
            {field.options?.map((option) => (
              <option
                key={`${field.field_key}-${option.value}`}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        ) : isTextarea ? (
          <textarea
            className={styles.formTextarea}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
            rows={3}
          />
        ) : (
          <input
            type={getInputTypeForField(field)}
            className={styles.formInput}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
          />
        )}
      </div>
    );
  };

  const renderPlaceholder = () => {
    const isCommunication = activeTab === 'communication';

    return (
      <div className={styles.placeholderState}>
        <div className={styles.placeholderIcon}>
          {isCommunication ? (
            <MessageSquareText size={28} />
          ) : (
            <History size={28} />
          )}
        </div>

        <h3 className={styles.placeholderTitle}>
          {isCommunication ? 'Communication Hub' : 'Activity Logs'}
        </h3>

        <p className={styles.placeholderText}>
          {isCommunication
            ? 'This area is ready for chat, notes, and conversation-related integrations.'
            : 'This area is ready for timeline, audit trail, and activity log integrations.'}
        </p>
      </div>
    );
  };

  const renderScanWelcome = () => {
    return (
      <>
        <div className={styles.introBlock}>
          <div className={styles.loaderOrb}>
            <AiLoader />
          </div>

          <h2 className={styles.introTitle}>Hello, Good Morning!</h2>
          <p className={styles.introText}>
            Welcome to Document Scan. Please upload a file or search and select
            a document to open it in the same workspace and run AI extraction.
          </p>
        </div>

        <div className={styles.workArea}>
          <button
            type="button"
            className={styles.uploadPane}
            onClick={handleUploadClick}
          >
            <UploadCloud size={28} className={styles.uploadIcon} />
            <span className={styles.uploadLink}>Click to Upload</span>

            <div className={styles.uploadHint}>
              Supported formats :
              <span className={styles.uploadChips}>
                <span className={styles.uploadChip}>PDF</span>
                <span className={styles.uploadChip}>JPEG</span>
                <span className={styles.uploadChip}>PNG</span>
              </span>
            </div>

            <div className={styles.uploadMax}>Max Size : 20.0 MB</div>
          </button>

          <div className={styles.orDivider}>OR</div>

          <div className={styles.docsPane}>
            <div className={styles.searchField}>
              <Search size={18} className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search for Document"
                value={searchDocument}
                onChange={(e) => setSearchDocument(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            <div className={styles.recentLabel}>Recent Uploaded :</div>

            <div className={styles.documentGrid}>
              {filteredDocumentList.slice(0, 4).map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  className={`${styles.documentCard} ${selectedDocumentId === doc.id
                    ? styles.documentCardActive
                    : ''
                  }`}
                  onClick={() => handleSelectDocument(doc.id)}
                >
                  <DocumentIcon kind={doc.kind} />

                  <div className={styles.documentMeta}>
                    <div className={styles.documentTitle}>{doc.title}</div>
                    <div className={styles.documentSize}>{doc.size}</div>
                  </div>

                  <span className={styles.sourceBadge}>
                    {doc.source === 'uploaded' ? 'Uploaded' : 'Recent'}
                  </span>
                </button>
              ))}

              {!filteredDocumentList.length && (
                <div className={styles.noDocuments}>
                  No matching documents found
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderDocumentPreview = () => {
    if (!selectedDocument) return null;

    const previewable = isPreviewableDocument(selectedDocument);

    if (!previewable || !selectedDocument.previewUrl) {
      return (
        <div className={styles.previewEmpty}>
          <div className={styles.previewEmptyIcon}>
            <DocumentIcon kind={selectedDocument.kind} />
          </div>
          <h4 className={styles.previewEmptyTitle}>Preview not available</h4>
          <p className={styles.previewEmptyText}>
            This item does not have a live file preview yet. Upload the actual
            PDF or image to preview and extract it here.
          </p>
        </div>
      );
    }

    if (selectedDocument.kind === 'pdf') {
      return (
        <div className={styles.pdfViewerShell}>
          <iframe
            title={selectedDocument.title}
            src={`${selectedDocument.previewUrl}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
            className={styles.viewerFrame}
          />
        </div>
      );
    }

    if (selectedDocument.kind === 'image') {
      return (
        <div className={styles.viewerImageWrap}>
          <img
            src={selectedDocument.previewUrl}
            alt={selectedDocument.title}
            className={styles.viewerImage}
          />
        </div>
      );
    }

    return (
      <div className={styles.previewEmpty}>
        <h4 className={styles.previewEmptyTitle}>Unsupported preview format</h4>
      </div>
    );
  };

  const renderSummarySuccessView = () => {
    return (
      <div className={styles.summarySuccessWrap}>
        <div className={styles.summaryResultCard}>
          <div className={styles.summaryResultHeader}>
            {selectedType?.label || 'AI'} AI Summary
          </div>

          <ul className={styles.summaryBulletList}>
            {aiSummaryBullets.map((line, index) => (
              <li key={`${line}-${index}`} className={styles.summaryBulletItem}>
                {line}
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.resultActions}>
          <button
            type="button"
            className={styles.scanAgainButton}
            onClick={resetToSelection}
          >
            <Sparkles size={14} />
            Scan Again
          </button>

          <button
            type="button"
            className={styles.extractToFieldsButton}
            onClick={() => setSuccessView('fields')}
          >
            Extract to Fields
            <ArrowRight size={16} />
          </button>

          <button
            type="button"
            className={styles.saveSummaryButton}
            onClick={() => void handleSaveSummaryToNotes()}
            disabled={isSavingSummary}
          >
            {isSavingSummary ? 'Saving Summary...' : 'Save Summary to Notes'}
          </button>
        </div>
      </div>
    );
  };

  const renderFieldsSuccessView = () => {
    return (
      <div className={styles.fieldsViewWrap}>
        <div className={styles.fieldsFormCard}>
          <div className={styles.structuredFieldsStack}>
            {structuredSections.map((section) => {
              const hasAnyContent =
                section.directFields.length > 0 || section.groups.length > 0;

              return (
                <div
                  key={section.section_key}
                  className={styles.structuredSectionCard}
                >
                  <div className={styles.structuredSectionHeader}>
                    <div className={styles.structuredSectionTitle}>
                      {section.section_title}
                    </div>
                  </div>

                  {!hasAnyContent ? (
                    <div className={styles.structuredEmptyState}>
                      No extracted fields available in this section.
                    </div>
                  ) : (
                    <>
                      {section.directFields.length > 0 && (
                        <div className={styles.structuredDirectFields}>
                          {section.directFields.map((field) =>
                            renderStructuredField(field),
                          )}
                        </div>
                      )}

                      {section.groups.length > 0 && (
                        <div className={styles.structuredGroupsStack}>
                          {section.groups.map((group) => (
                            <div
                              key={group.key}
                              className={styles.structuredGroupCard}
                            >
                              <div className={styles.structuredGroupTitle}>
                                {group.title}
                              </div>

                              {group.fields.length ? (
                                <div className={styles.structuredGroupFields}>
                                  {group.fields.map((field) =>
                                    renderStructuredField(field),
                                  )}
                                </div>
                              ) : (
                                <div className={styles.structuredEmptySubgroup}>
                                  No extracted fields in this subsection.
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.fieldsFooterActions}>
          <button
            type="button"
            className={styles.cancelFieldsButton}
            onClick={() => setSuccessView('summary')}
          >
            Cancel
          </button>

          <button
            type="button"
            className={styles.saveEmrButton}
            onClick={() => void handleSaveToEMR()}
            disabled={isSavingToEmr || isCheckingCycle}
          >
            {isCheckingCycle
              ? 'Checking Active Cycle...'
              : isSavingToEmr
                ? 'Saving...'
                : 'Save to EMR'}
          </button>
        </div>
      </div>
    );
  };

  const renderSidebarBody = () => {
    return (
      <div className={styles.sidebarBody}>
        {sidebarMode === 'idle' && (
          <>
            <div className={styles.welcomeState}>
              <div className={styles.welcomeOrbWrap}>
                <div className={styles.welcomeLoaderOverlay}>
                  <AiLoader />
                </div>
              </div>

              <h3 className={styles.welcomeTitle}>Welcome to AI Extraction</h3>
              <p className={styles.welcomeText}>
                Please search &amp; select a page from below to run an AI scan
                &amp; extract data
              </p>
            </div>

            <div className={styles.searchWrap}>
              <Search size={18} className={styles.searchIcon} />
              <input
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search EMR page"
                className={styles.searchInput}
              />
            </div>

            <div className={styles.recommendedLabel}>Recommended Pages :</div>

            <div className={styles.chipsWrap}>
              {filteredOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={styles.pageChip}
                  onClick={() => void runExtraction(option)}
                  title={option.description}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {!filteredOptions.length && (
              <div className={styles.inlinePreviewEmpty}>
                No matching EMR page found.
              </div>
            )}

            {extractionError && (
              <div className={styles.errorBox}>{extractionError}</div>
            )}
          </>
        )}

        {sidebarMode === 'loading' && (
          <div className={styles.loadingOnlyState}>
            <div className={styles.loadingOnlyOrbWrap}>
              <div className={styles.loadingOnlyLoaderOverlay}>
                <AiLoader />
              </div>
            </div>

            <h3 className={styles.loadingTitle}>Extracting Clinical Data</h3>
            <p className={styles.loadingText}>
              Running AI extraction for{' '}
              <span className={styles.highlightText}>
                {selectedType?.label ?? 'selected page'}
              </span>
              . Please wait while we analyze the document.
            </p>
          </div>
        )}

        {sidebarMode === 'success' &&
          (successView === 'summary'
            ? renderSummarySuccessView()
            : renderFieldsSuccessView())}

        {sidebarMode === 'error' && (
          <div className={styles.loadingOnlyState}>
            <div className={styles.loadingOnlyOrbWrap}>
              <div className={styles.loadingOnlyLoaderOverlay}>
                <AiLoader />
              </div>
            </div>

            <h3 className={styles.loadingTitle}>Extraction Failed</h3>
            <div className={styles.errorBox}>{extractionError}</div>

            <div className={styles.resultActions}>
              <button
                type="button"
                className={styles.scanAgainButton}
                onClick={resetToSelection}
              >
                <Sparkles size={14} />
                Scan Again
              </button>

              <button
                type="button"
                className={styles.extractToFieldsButton}
                onClick={() => void handleRetry()}
              >
                Retry Extraction
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {infoMessage ? (
          <div className={styles.infoNote}>{infoMessage}</div>
        ) : null}
      </div>
    );
  };

  const renderScanWorkspace = () => {
    return (
      <div className={styles.scanLayout}>
        <div className={styles.viewerCard}>
          <div className={styles.viewerHeader}>
            <div className={styles.viewerHeading}>
              <button
                type="button"
                className={styles.backButton}
                onClick={handleBackToLibrary}
              >
                <ArrowLeft size={16} />
              </button>

              <div>
                <div className={styles.viewerTitle}>Document Viewer</div>
                <div className={styles.viewerSubTitleText}>
                  {selectedDocument?.title || 'No document selected'}{' '}
                  {selectedDocument?.size ? `(${selectedDocument.size})` : ''}
                </div>
              </div>
            </div>

            <button
              type="button"
              className={styles.uploadAnotherButton}
              onClick={handleUploadClick}
            >
              <UploadCloud size={16} />
              Upload another
            </button>
          </div>

          <div className={styles.viewerCanvas}>{renderDocumentPreview()}</div>

          <div className={styles.documentRail}>
            <div className={styles.documentRailHeader}>
              <div className={styles.documentRailTitle}>
                Available Documents
              </div>

              <div className={styles.documentRailSearch}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search documents"
                  value={searchDocument}
                  onChange={(e) => setSearchDocument(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </div>

            <div className={styles.documentRailList}>
              {filteredDocumentList.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  className={`${styles.railDocCard} ${selectedDocumentId === doc.id
                    ? styles.railDocCardActive
                    : ''
                  }`}
                  onClick={() => handleSelectDocument(doc.id)}
                >
                  <DocumentIcon kind={doc.kind} />
                  <div className={styles.railDocMeta}>
                    <div className={styles.railDocTitle}>{doc.title}</div>
                    <div className={styles.railDocSize}>{doc.size}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.extractCard}>
          <div className={styles.extractHeader}>
            <div className={styles.extractTitle}>AI Clinical Extraction</div>
          </div>

          {renderSidebarBody()}
        </div>
      </div>
    );
  };

  const renderCommunicationHub = () => {
    return (
      <>
        <div className={styles.communicationHubLayout}>
          <div className={styles.communicationLeftColumn}>
            <div className={styles.communicationTopBar}>
              <div className={styles.communicationFilterRow}>
                {COMMUNICATION_FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`${styles.communicationFilterChip} ${communicationFilter === option.key
                      ? styles.communicationFilterChipActive
                      : ''
                    }`}
                    onClick={() => setCommunicationFilter(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className={styles.communicationTopBarRight}>
                <div className={styles.communicationSearchWrap}>
                  <Search size={16} className={styles.searchIcon} />
                  <input
                    type="text"
                    value={communicationSearch}
                    onChange={(e) => setCommunicationSearch(e.target.value)}
                    placeholder="Search by communication name, participants name"
                    className={styles.searchInput}
                  />
                </div>

                <button
                  type="button"
                  className={styles.communicationFilterIconButton}
                  aria-label="Open communication filters"
                >
                  <SlidersHorizontal size={16} />
                </button>
              </div>
            </div>

            <div className={styles.communicationTimelineCard}>
              {communicationLoading ? (
                <div className={styles.loadingState}>
                  <div className={styles.loaderOrb}>
                    <AiLoader />
                  </div>
                  <p className={styles.loadingText}>
                    Loading communication logs...
                  </p>
                </div>
              ) : communicationError ? (
                <div className={styles.errorBanner}>
                  <AlertCircle size={18} />
                  <span>{communicationError}</span>
                </div>
              ) : !groupedCommunicationLogs.length ? (
                <div className={styles.placeholderState}>
                  <div className={styles.placeholderIcon}>
                    <Mic size={28} />
                  </div>
                  <h3 className={styles.placeholderTitle}>
                    No communication logs found
                  </h3>
                  <p className={styles.placeholderText}>
                    Voice-to-text logs will appear here.
                  </p>
                </div>
              ) : (
                <div className={styles.communicationTimelineScroll}>
                  {groupedCommunicationLogs.map((group) => (
                    <div
                      key={group.label}
                      className={styles.communicationDateGroup}
                    >
                      <div className={styles.communicationDateHeader}>
                        <span className={styles.communicationDateChip}>
                          {group.label}
                        </span>
                        <div className={styles.communicationDateDivider} />
                      </div>

                      <div className={styles.communicationTimelineStack}>
                        {group.items.map((log) => {
                          const channelMeta =
                            COMMUNICATION_CHANNEL_OPTIONS.find(
                              (item) => item.key === log.communicationType,
                            );
                          const Icon = channelMeta?.icon || Mic;

                          return (
                            <div
                              key={log.rowKey}
                              className={styles.communicationTimelineRow}
                            >
                              <div className={styles.communicationTimelineTime}>
                                {log.timeLabel}
                              </div>

                              <div
                                className={
                                  styles.communicationTimelineMarkerColumn
                                }
                              >
                                <div
                                  className={styles.communicationTimelineLine}
                                />
                                <div
                                  className={
                                    styles.communicationTimelineIconBubble
                                  }
                                >
                                  <Icon size={16} />
                                </div>
                              </div>

                              <div
                                className={`${styles.communicationEntryCard} ${selectedCommunicationLogId === log.rowKey
                                  ? styles.communicationEntryCardActive
                                  : ''
                                }`}
                                role="button"
                                tabIndex={0}
                                onClick={() =>
                                  handleSelectCommunicationLog(log)
                                }
                                onKeyDown={(event) => {
                                  if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                  ) {
                                    event.preventDefault();
                                    handleSelectCommunicationLog(log);
                                  }
                                }}
                              >
                                <div className={styles.communicationEntryTitle}>
                                  {log.titleText}
                                </div>

                                <div
                                  className={styles.communicationEntrySnippet}
                                >
                                  {log.snippetText}
                                </div>

                                <div
                                  className={styles.communicationEntryDivider}
                                />

                                <div
                                  className={styles.communicationEntryFooter}
                                >
                                  <div
                                    className={
                                      styles.communicationEntryMetaWrap
                                    }
                                  >
                                    <div
                                      className={
                                        styles.communicationEntryMetaBlock
                                      }
                                    >
                                      <span
                                        className={
                                          styles.communicationEntryMetaLabel
                                        }
                                      >
                                        Source
                                      </span>
                                      <span
                                        className={
                                          styles.communicationEntryMetaValue
                                        }
                                      >
                                        {log.source ||
                                          channelMeta?.label ||
                                          'Voice to Text'}
                                      </span>
                                    </div>

                                    <div
                                      className={
                                        styles.communicationEntryMetaBlock
                                      }
                                    >
                                      <span
                                        className={
                                          styles.communicationEntryMetaLabel
                                        }
                                      >
                                        Doctor
                                      </span>
                                      <span
                                        className={
                                          styles.communicationEntryMetaValue
                                        }
                                      >
                                        {log.doctor_name || '-'}
                                      </span>
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    className={
                                      styles.communicationViewTranscriptButton
                                    }
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenTranscriptModal(log);
                                    }}
                                  >
                                    <FileText size={16} />
                                    View Transcript
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={styles.communicationComposerArea}>
            <div className={styles.communicationToolRail}>
              {COMMUNICATION_TOOL_OPTIONS.map((tool) => {
                const Icon = tool.icon;

                return (
                  <button
                    key={tool.key}
                    type="button"
                    title={tool.label}
                    disabled={!tool.enabled}
                    className={`${styles.communicationToolButton} ${activeCommunicationTool === tool.key
                      ? styles.communicationToolButtonActive
                      : ''
                    } ${!tool.enabled ? styles.communicationToolButtonDisabled : ''}`}
                    onClick={() => handleSelectCommunicationTool(tool.key)}
                  >
                    <Icon size={18} />
                  </button>
                );
              })}
            </div>

            <div className={styles.communicationToolPanel}>
              {activeCommunicationTool === 'voice_to_text' && (
                <EmbeddedVoiceToTextWorkbench
                  key={`voice-workbench-${voiceWorkbenchSeedKey}`}
                  patientId={resolvedPatientId}
                  transcriptSeed={communicationTranscriptSeed}
                  onSaved={() => void loadCommunicationLogs()}
                  title="AI Voice Data Entry"
                />
              )}
            </div>
          </div>
        </div>

        {isTranscriptModalOpen && transcriptModalLog && (
          <div className={styles.transcriptModalBackdrop}>
            <div className={styles.transcriptModalCard}>
              <div className={styles.transcriptModalHeader}>
                <h3 className={styles.transcriptModalTitle}>Edit Transcript</h3>

                <button
                  type="button"
                  className={styles.transcriptModalCloseButton}
                  onClick={handleCloseTranscriptModal}
                  aria-label="Close transcript popup"
                >
                  <X size={18} />
                </button>
              </div>

              <div className={styles.transcriptModalDivider} />

              <div className={styles.transcriptModalBody}>
                <div className={styles.transcriptModalFieldWrap}>
                  <span className={styles.transcriptModalFieldLabel}>
                    Transcript
                  </span>

                  <textarea
                    value={transcriptModalValue}
                    onChange={(e) => setTranscriptModalValue(e.target.value)}
                    className={styles.transcriptModalTextarea}
                  />
                </div>
              </div>

              <div className={styles.transcriptModalActions}>
                <button
                  type="button"
                  className={styles.transcriptModalSecondaryButton}
                  onClick={handleCloseTranscriptModal}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className={styles.transcriptModalSecondaryButton}
                  onClick={handleSaveTranscriptModal}
                >
                  Save
                </button>

                <button
                  type="button"
                  className={styles.transcriptModalPrimaryButton}
                  onClick={handleScanTranscriptFromModal}
                >
                  Scan for AI Insights
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={styles.page}
      style={
        isModal
          ? {
            height: '100%',
            overflowY: 'auto',
            padding: '20px',
            background: '#fff',
          }
          : undefined
      }
    >
      <div
        className={styles.pageTitleRow}
        style={
          isModal
            ? {
              position: 'sticky',
              top: 0,
              zIndex: 20,
              background: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingBottom: '12px',
            }
            : undefined
        }
      >
        <h1 className={styles.pageTitle}>Document Intelligence</h1>

        {isModal && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close document intelligence"
            style={{
              border: '1px solid #E5E7EB',
              background: '#FFFFFF',
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            }}
          >
            <X size={18} />
          </button>
        )}
      </div>

      {!isModal && (
        <div className={styles.summaryCard}>
          <div className={styles.avatarCluster}>
            <AvatarBadge
              image={patientSummary.patientAvatar}
              name={patientSummary.name}
            />
            <AvatarBadge
              image={patientSummary.partnerAvatar}
              name={`${patientSummary.name} Partner`}
              className={styles.partnerAvatarShift}
            />
          </div>

          <div className={styles.summaryGrid}>
            <div className={styles.summaryItemLarge}>
              <span className={styles.summaryLabel}>Patient Name</span>
              <span className={styles.summaryValueLarge}>
                {patientSummary.name}
              </span>
            </div>

            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Age</span>
              <span className={styles.summaryValue}>{patientSummary.age}</span>
            </div>

            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Sex Assigned At Birth</span>
              <span className={styles.summaryValue}>
                {patientSummary.sexAssignedAtBirth}
              </span>
            </div>

            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>MRN</span>
              <span className={styles.summaryValue}>{patientSummary.mrn}</span>
            </div>

            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Allergy</span>
              <span className={styles.summaryValue}>
                {patientSummary.allergy}
              </span>
            </div>

            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Last Modified</span>
              <span className={styles.summaryValue}>
                {patientSummary.lastModified}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className={styles.tabRow}>
        <button
          type="button"
          className={`${styles.tabButton} ${activeTab === 'communication' ? styles.activeTab : ''
          }`}
          onClick={() => setActiveTab('communication')}
        >
          Communication Hub
        </button>

        <button
          type="button"
          className={`${styles.tabButton} ${activeTab === 'scan' ? styles.activeTab : ''
          }`}
          onClick={() => setActiveTab('scan')}
        >
          Document Scan
        </button>

        <button
          type="button"
          className={`${styles.tabButton} ${activeTab === ('activity' as TabKey) ? styles.activeTab : ''
          }`}
          onClick={() => setActiveTab('activity' as TabKey)}
        >
          Activity Logs
        </button>
      </div>

      <div className={styles.surface}>
        <div className={styles.gradientLayer}>
          <AiGradient />
        </div>

        <div
          className={`${styles.surfaceContent} ${activeTab === 'communication' ||
              (activeTab === 'scan' && selectedDocument)
            ? styles.surfaceContentWorkspace
            : ''
          }`}
        >
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.loaderOrb}>
                <AiLoader />
              </div>
              <p className={styles.loadingText}>Loading patient details...</p>
            </div>
          ) : error ? (
            <div className={styles.errorBanner}>
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          ) : activeTab === 'scan' ? (
            <>
              {selectedDocument ? renderScanWorkspace() : renderScanWelcome()}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFilesSelected}
              />
            </>
          ) : activeTab === 'communication' ? (
            renderCommunicationHub()
          ) : (
            renderPlaceholder()
          )}
        </div>
      </div>
      {showNoActiveCycleModal && (
        <div className={styles.noCycleModalBackdrop}>
          <div
            className={styles.noCycleModalCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="no-cycle-modal-title"
          >
            <div className={styles.noCycleModalHeader}>
              <h3
                id="no-cycle-modal-title"
                className={styles.noCycleModalTitle}
              >
                {selectedType?.documentType === 'embryo_grading'
                  ? 'Edit Embryo Information Details'
                  : 'Cycle Validation'}
              </h3>

              <button
                type="button"
                className={styles.noCycleModalCloseButton}
                onClick={handleCloseNoCycleModal}
                disabled={isSavingSummary}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles.noCycleModalDivider} />

            <div className={styles.noCycleModalBody}>
              <div className={styles.noCycleModalHighlightBox}>
                <span className={styles.noCycleModalFloatingLabel}>
                  Active Cycle Status
                </span>
                <div className={styles.noCycleModalHighlightValue}>
                  User does not have any active cycle.
                </div>
              </div>

              <p className={styles.noCycleModalMessage}>
                This extracted data cannot be saved to EMR because no active
                cycle exists for this patient. You can save the extracted date
                to AI Summary instead.
              </p>
            </div>

            <div className={styles.noCycleModalActions}>
              <button
                type="button"
                className={styles.noCycleModalSecondaryButton}
                onClick={handleCloseNoCycleModal}
                disabled={isSavingSummary}
              >
                Cancel
              </button>

              <button
                type="button"
                className={styles.noCycleModalPrimaryButton}
                onClick={() => void handleSaveToSummaryBecauseNoActiveCycle()}
                disabled={isSavingSummary}
              >
                {isSavingSummary ? 'Saving...' : 'Save to Summary'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentIntelligencePatientDetail;
